// In-world chat input bar. Press Enter (when nothing else is typing) to open a pixel-styled text
// bar at the bottom of the screen, above the HUD buttons. Type a line; Enter sends it (the server
// echoes it back as a chat bubble above your character), Esc cancels. While the bar is open the
// game ignores movement keys.
//
// Ghost (admin) only: the "SAY>" prefix is clickable to toggle into "SERVER>" mode — a line sent
// then becomes a server notification that pops up on every connected player's screen.

import { CHAT_MAX_LEN, SERVER_NOTICE_MAX_LEN } from "@ghostland/shared";
import { injectTheme } from "./theme.js";

export interface ChatActions {
  send: (text: string) => void;
  /** Broadcast a server notification (only used when SERVER mode is enabled for this client). */
  announce?: (text: string) => void;
}
let actions: ChatActions | null = null;
export function setChatActions(a: ChatActions): void { actions = a; }

let canServer = false;                  // this client is Ghost → may switch to SERVER mode
let mode: "say" | "server" = "say";
/** Allow this client to toggle the chat bar into SERVER-broadcast mode (Ghost/admin only). */
export function enableServerMode(): void { canServer = true; }

const STYLE_ID = "ghostland-chat-style";
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Silkscreen:wght@400;700&display=swap');
#gl-chat-bar {
  position: fixed; left: 50%; bottom: 84px; transform: translateX(-50%); z-index: 26;
  display: none; align-items: center; gap: 8px; width: min(460px, 90vw); padding: 9px 12px;
  background: #000; border: 2px solid #f4f4f4; image-rendering: pixelated;
  box-shadow: 0 0 0 2px #000, 4px 4px 0 rgba(0,0,0,.6);
}
#gl-chat-bar.open { display: flex; }
#gl-chat-bar.server { border-color: #ffcf4d; box-shadow: 0 0 0 2px #000, 4px 4px 0 rgba(0,0,0,.6), 0 0 10px 1px rgba(255,207,77,.45); }
#gl-chat-bar .pre { font-family: 'Silkscreen', ui-monospace, monospace; font-size: 11px; color: #79d18a; user-select: none; }
#gl-chat-bar.server .pre { color: #ffcf4d; }
#gl-chat-bar.switchable .pre { cursor: pointer; }
#gl-chat-bar.switchable .pre:hover { text-decoration: underline; }
#gl-chat-input {
  flex: 1; background: transparent; border: none; outline: none; color: #f4f4f4;
  font-family: ui-monospace, "SFMono-Regular", Menlo, monospace; font-size: 13px; letter-spacing: .04em;
}
#gl-chat-input::placeholder { color: #4a4a4a; }
#gl-chat-bar .hint { font-family: 'Silkscreen', ui-monospace, monospace; font-size: 7px; color: #5a5a5a; letter-spacing: 1px; white-space: nowrap; }
`;

let bar: HTMLDivElement | null = null;
let input: HTMLInputElement | null = null;
let pre: HTMLSpanElement | null = null;
let hint: HTMLSpanElement | null = null;

/** Sync the prefix label, colours, placeholder + length limit to the current mode. */
function refreshMode(): void {
  if (!bar || !input || !pre || !hint) return;
  const server = mode === "server";
  bar.classList.toggle("server", server);
  bar.classList.toggle("switchable", canServer);
  pre.textContent = server ? "SERVER>" : "SAY>";
  input.placeholder = server ? "server announcement…" : "type a message…";
  input.maxLength = server ? SERVER_NOTICE_MAX_LEN : CHAT_MAX_LEN;
  hint.textContent = canServer ? "CLICK SAY/SERVER · ENTER · ESC" : "ENTER · ESC";
}

function ensure(): void {
  injectTheme();
  if (!document.getElementById(STYLE_ID)) {
    const s = document.createElement("style"); s.id = STYLE_ID; s.textContent = CSS; document.head.appendChild(s);
  }
  if (bar) return;
  bar = document.createElement("div");
  bar.id = "gl-chat-bar";
  bar.innerHTML = `<span class="pre">SAY&gt;</span><input id="gl-chat-input" maxlength="${CHAT_MAX_LEN}" placeholder="type a message…" autocomplete="off" /><span class="hint">ENTER · ESC</span>`;
  document.body.appendChild(bar);
  input = bar.querySelector("#gl-chat-input");
  pre = bar.querySelector(".pre");
  hint = bar.querySelector(".hint");
  // Ghost can click the prefix to flip SAY ↔ SERVER. preventDefault keeps the input focused.
  pre!.addEventListener("mousedown", (e) => {
    if (!canServer) return;
    e.preventDefault();
    mode = mode === "say" ? "server" : "say";
    refreshMode();
    input!.focus();
  });
  input!.addEventListener("keydown", (e) => {
    e.stopPropagation(); // don't let the game see keystrokes while typing
    if (e.key === "Enter") {
      const t = input!.value.trim();
      if (t) { if (mode === "server" && canServer) actions?.announce?.(t); else actions?.send(t); }
      closeChat();
    } else if (e.key === "Escape") {
      closeChat();
    }
  });
}

/** Open + focus the chat input. */
export function openChat(): void {
  ensure();
  refreshMode();
  bar!.classList.add("open");
  input!.value = "";
  input!.focus();
}

export function closeChat(): void {
  bar?.classList.remove("open");
  input?.blur();
}

export function isChatOpen(): boolean {
  return bar?.classList.contains("open") ?? false;
}
